import { useEffect, useMemo } from 'react';

import { useProviderFactory } from '@atlaskit/editor-common/provider-factory/context';
import ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { ProviderHandler } from '@atlaskit/editor-common/provider-factory/types';

const EXTENSION_PROVIDER = 'extensionProvider';

/**
 * Derives a private `ProviderFactory` for a nested LCM renderer from the ambient page factory.
 *
 * Two independent reasons this cannot just be the ambient factory:
 *
 * 1. `mergeDataProviders` in `ADFRendererComponent` *mutates* whatever factory it is handed, calling
 *    `setProvider` with a freshly constructed mediaProvider / cardProvider / taskDecisionProvider /
 *    mentionProvider / profilecardProvider. Passing the page factory therefore replaces those
 *    providers for the whole page and, because `setProvider` notifies every subscriber, re-renders
 *    each page-level media node, smart link, emoji and task item — a page-wide provider swap at
 *    exactly the moment the LCM takeover is supposed to be invisible.
 *
 * 2. The nested renderer still needs `extensionProvider`, since that is the only route by which
 *    nested extensions reach their manifests. Platform's `WithProviders` subscribes in its
 *    constructor and never re-subscribes when the factory identity changes, so the provider must be
 *    on the factory during the *first* render; attaching it later never reaches anything already
 *    mounted, which would leave every `null`-returning handler with a dead manifest fall-through.
 *
 * Hence a child factory, seeded during render so it is populated before any nested `WithProviders`
 * is constructed, kept in sync with the parent afterwards, and absorbing all of
 * `mergeDataProviders`' writes so they never reach the page.
 */
export const useNestedLcmProviderFactory = (): ProviderFactory => {
	const parentProviderFactory = useProviderFactory();

	const providerFactory = useMemo(() => {
		// `subscribe` is the only way to read a provider's value — `hasProvider` returns just a boolean
		// — so it is paired with an immediate `unsubscribe`. That keeps the read self-cleaning: React
		// may invoke a `useMemo` factory more than once for a single render (it does in Strict Mode),
		// and a subscription registered here would outlive the discarded value, since the effect below
		// can only ever clean up the one from the committed render.
		const readParentExtensionProvider = () => {
			const probe: ProviderHandler = () => {};
			const provider = parentProviderFactory.subscribe(EXTENSION_PROVIDER, probe);
			parentProviderFactory.unsubscribe(EXTENSION_PROVIDER, probe);

			return provider;
		};

		const inheritedProvider = readParentExtensionProvider();

		return ProviderFactory.create(
			inheritedProvider ? { [EXTENSION_PROVIDER]: inheritedProvider } : {},
		);
	}, [parentProviderFactory]);

	// Forward later changes. `subscribe` invokes the handler with the current value, so nothing is
	// missed between the seeding above and this effect.
	useEffect(() => {
		const forwardExtensionProvider: ProviderHandler = (_name, provider) => {
			providerFactory.setProvider(EXTENSION_PROVIDER, provider);
		};

		void parentProviderFactory.subscribe(EXTENSION_PROVIDER, forwardExtensionProvider);

		return () => parentProviderFactory.unsubscribe(EXTENSION_PROVIDER, forwardExtensionProvider);
	}, [parentProviderFactory, providerFactory]);

	return providerFactory;
};
